import type { HudCharacterData } from '../../lib/character-hud-store';
import { useI18n } from '../../i18n';

/**
 * Batch B (2026-07-03): death-state surface for the COMPACT hud states.
 * `deathSaves.status` is server-authoritative (sheet.dnd5e.deathSaveState via
 * the adapter — see the type doc in character-hud-store); until now only the
 * full-sheet DefensesSection read it, so pill/mini/combat showed a perfectly
 * ordinary HUD for a dying or dead character (ADR-010 §8.10 wanted a death
 * state with input blocked).
 */
export type HudDeathStatus = 'alive' | 'dying' | 'stable' | 'dead';

export function hudDeathStatus(data: HudCharacterData): HudDeathStatus {
  // Optional chain: legacy persisted shapes / older test fixtures omit
  // deathSaves entirely (the type doc explicitly tolerates that).
  return data.deathSaves?.status ?? 'alive';
}

/** Mutating input must be blocked when the character is dead (§8.10). */
export function isDeathInputBlocked(data: HudCharacterData): boolean {
  return hudDeathStatus(data) === 'dead';
}

/** At 0 hit points (dying or stable) the character is Unconscious: no attacks, no spells (re-pass Н18). */
export function isUnconsciousAtZero(data: HudCharacterData): boolean {
  const st = hudDeathStatus(data);
  return st === 'dying' || st === 'stable';
}

/**
 * Horizontal status strip for Mini/Combat states and CombatTab.
 * Renders nothing while the character is alive.
 */
export function DeathBanner({ data }: { data: HudCharacterData }) {
  const { t } = useI18n();
  const status = hudDeathStatus(data);
  if (status === 'alive') return null;

  const { successes = 0, failures = 0 } = data.deathSaves ?? {};
  const palette =
    status === 'dead'
      ? { bg: 'var(--paper-ink)', fg: 'var(--paper-bg)' }
      : status === 'dying'
        ? { bg: 'var(--paper-bordo)', fg: 'var(--paper-bg)' }
        : { bg: 'var(--paper-bg-3)', fg: 'var(--paper-ink)' }; // stable

  const label =
    status === 'dead' ? t('characterHud.deathDead')
    : status === 'dying' ? t('characterHud.deathDying')
    : t('characterHud.deathStable');

  return (
    <div
      data-testid="death-banner"
      data-death-status={status}
      role="status"
      className="text-label" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '3px 8px',
        background: palette.bg,
        color: palette.fg,
        fontFamily: 'JetBrains Mono, monospace',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      <span>☠ {label}</span>
      {status === 'dying' && (
        <span aria-label={t('characterHud.deathSavesTally')}>
          ✓{successes} ✗{failures}
        </span>
      )}
    </div>
  );
}
